import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { UserContext } from "../../../contexts/user.context";
import { Get, handleError } from "../../../utils/fetch.util";
import NoData from "../../../components/noData/noData.component";
import BackButton from "../../../components/modals/AddAssetModal/BackButton/BackButton";
import { FiSearch } from "react-icons/fi";
import StaffCard from "../../../components/staffCard/staffCard.component";
import { differenceInMilliseconds, format } from "date-fns";
import { StaffGrid } from "../staff/staff.styles";
import ConfirmationCard from "./ConfirmationCard";
import ReactPaginate from "react-paginate";
import { GrFormNext, GrFormPrevious } from "react-icons/gr";

interface AttendanceRecord {
  id: number;
  attendance: boolean;
  leaving: boolean;
  time_on_site: string | null;
  createdAt: string;
  updatedAt: string;
  location: string;
  location_out: string;
  late: boolean;
  attendance_type: string;
  asdc_client_staff_id: number;
  user_id: number;
  shift_id: number;
  site_id: number | null;
  security_post_id: number;
  deleted: boolean;
  asdc_client_staff: {
    asdc_client_staff_id: number;
    asdc_client_staff_name: string;
    asdc_client_staff_qrcode: string;
    asdc_client_staff_qrcode_url: string;
    asdc_client_profile_pic_url: string;
    staff_serial_number: string | null;
  };
}

interface ConfirmationRecord {
  id: number;
  department_workShift_id: number;
  status: string;
  type: string;
  staff_id: number;
  user_id: number;
  client_id: number;
  security_post_id: number;
  site_id: number;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
  staff: {
    asdc_client_staff_id: number;
    asdc_client_staff_name: string;
    asdc_client_staff_qrcode: string;
    asdc_client_staff_qrcode_url: string;
    asdc_client_profile_pic_url: string;
    staff_serial_number: string | null;
  };
}

export default function SecurityPlansAttendance() {
  const { t } = useTranslation();
  const { getValidToken } = useContext(UserContext);
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [securityPostId, setSecurityPostId] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [confirmationRecords, setConfirmationRecords] = useState<any[]>([]);
  const [filters, setFilters] = useState<{ search: string }>({ search: "" });
  const [displayMode, setDisplayMode] = useState<"attendance" | "confirmation">(
    "attendance"
  );

  const [pagination, setPagination] = useState<{
    page: number;
    pageItemsCount: number;
    attendanceCount: number;
    confirmationCount: number;
  }>({ page: 1, pageItemsCount: 10, attendanceCount: 0, confirmationCount: 0 });

  console.log(pagination)

  useEffect(() => {
    // Parse URL parameters
    const params = new URLSearchParams(location.search);
    const postId = params.get("securityPostId");
    const createdAt = params.get("createdAt");

    if (postId) {
      setSecurityPostId(postId);
    }

    if (createdAt) {
      // Format the date as needed for the API
      const dateObj = new Date(createdAt);
      setDate(dateObj.toISOString().split("T")[0]); // Format as YYYY-MM-DD
    }
  }, [location.search]);

  const fetchAttendanceRecords = async () => {
    if (!securityPostId) return;

    setIsLoading(true);
    const query: string[] = [];

    query.push(`security_post_id=${securityPostId}`);
    query.push(`page=${pagination.page}`);
    query.push(`pageItemsCount=${pagination.pageItemsCount}`);

    if (date) query.push(`date=${date}`);
    if (filters.search.length > 0) query.push(`search=${filters.search}`);

    try {
      const res = await Get(
        `security-post/get-security-post-attendance?${query.join("&")}`,
        getValidToken()
      );

      if (res?.error || !res?.data) {
        handleError(res);
        return;
      }

      setAttendanceRecords(res.data?.staffAttendances);
      setPagination((prev) => ({ ...prev, attendanceCount: res.data.count || 0 }));
    } catch (error) {
      console.error("Error fetching attendance records:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchConfirmationRecords = async () => {
    if (!securityPostId) return;

    setIsLoading(true);
    const query: string[] = [];

    query.push(`security_post_id=${securityPostId}`);
    query.push(`page=${pagination.page}`);
    query.push(`pageItemsCount=${pagination.pageItemsCount}`);

    if (date) query.push(`date=${date}`);
    if (filters.search.length > 0) query.push(`search=${filters.search}`);

    try {
      const res = await Get(
        `security-post/get-security-post-confirmation?${query.join("&")}`,
        getValidToken()
      );

      if (res?.error || !res?.data) {
        handleError(res);
        return;
      }

      setConfirmationRecords(res.data?.staffNotifications);
      setPagination((prev) => ({ ...prev, confirmationCount: res.data.count || 0 }));
    } catch (error) {
      console.error("Error fetching confirmation records:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (securityPostId) {
      fetchAttendanceRecords();
      fetchConfirmationRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityPostId, date, pagination.page, filters.search]);

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  return (
    <div className="container bg-white p-6 px-8">
      <div className="flex items-center gap-3 mt-2 mb-6">
        <BackButton />
        <h1 className="text-5xl">{t("security_plans_attendance")}</h1>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-5">
        {/* Search Filter */}
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder={t("search")}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            value={filters.search}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }));
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          />
          <button
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
            }}
            onClick={() => {
              setFilters((prev) => ({ ...prev, search: prev.search }));
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            aria-label="Search"
            tabIndex={-1}
            type="button"
          >
            <FiSearch size={20} />
          </button>
        </div>

        {/* Date Filter */}
        {date && (
          <div className="flex items-center gap-2">
            <label
              htmlFor="date-filter"
              className="text-sm font-medium text-gray-700"
            >
              {t("date")}:
            </label>
            <input
              id="date-filter"
              type="date"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            />
          </div>
        )}

        <div className="ml-4">
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            value={displayMode}
            onChange={(e) =>
              setDisplayMode(e.target.value as "attendance" | "confirmation")
            }
          >
            <option value="attendance">{t("attendance_records")}</option>
            <option value="confirmation">{t("confirmation_records")}</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-lg">{t("loading")}...</div>
        </div>
      ) : (
        <>
          {displayMode === "attendance" && (
            /* Attendance Records */
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">
                {t("attendance_records")}
              </h2>
              {attendanceRecords?.length === 0 ? (
                <NoData message={t("no_attendance_records_found")} />
              ) : (
                <StaffGrid className="space-x-6">
                  {attendanceRecords.map((attendee) => {
                    function getDifferenceBetweenDates(
                      date1: string,
                      date2: string
                    ) {
                      const diffInMilliseconds = differenceInMilliseconds(
                        date2,
                        date1
                      );

                      const totalSeconds = Math.floor(
                        diffInMilliseconds / 1000
                      );
                      const totalMinutes = Math.floor(totalSeconds / 60);
                      const totalHours = Math.floor(totalMinutes / 60);
                      const totalDays = Math.floor(totalHours / 24);

                      const seconds = totalSeconds % 60;
                      const minutes = totalMinutes % 60;
                      const hours = totalHours % 24;
                      const days = totalDays;

                      return { days, hours, minutes, seconds };
                    }

                    function formatDuration({
                      // days,
                      hours,
                      minutes,
                      seconds,
                    }: {
                      // days: number;
                      hours: number;
                      minutes: number;
                      seconds: number;
                    }) {
                      const formattedHours = String(hours).padStart(2, "0");
                      const formattedMinutes = String(minutes).padStart(2, "0");
                      const formattedSeconds = String(seconds).padStart(2, "0");

                      return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
                    }

                    const difference = getDifferenceBetweenDates(
                      attendee.createdAt,
                      attendee.updatedAt
                    );
                    const formattedDifference = formatDuration(difference);

                    return (
                      <StaffCard
                        member={{...attendee?.asdc_client_staff, attendance: attendee.attendance}}
                        key={`${attendee?.asdc_client_staff_id}`}
                        deleteMember={null}
                        startEditingMember={null}
                        showQrCode={null}
                        timeIn={
                          attendee.createdAt
                            ? format(attendee.createdAt, "hh:mm a")
                            : "Absent"
                        }
                        timeOut={
                          attendee?.leaving
                            ? format(attendee.updatedAt, "hh:mm a")
                            : attendee.createdAt
                              ? "Still In"
                              : "Absent"
                        }
                        showStatus={true}
                        duration={
                          formattedDifference.includes("NaN")
                            ? "00:00:00"
                            : formattedDifference
                        }
                        isAttendee={true}
                        location={attendee.location || undefined}
                        locationOut={
                          attendee?.leaving
                            ? attendee?.location_out.length > 0
                              ? attendee?.location_out
                              : undefined
                            : undefined
                        }
                        // setOpenStaffAttendanceCount={setOpenStaffAttendanceCount}
                        // setStaffData={setStaffData}
                      />
                    );
                  })}
                </StaffGrid>
              )}
            </div>
          )}

          {displayMode === "confirmation" && (
            /* Confirmation Records */
            <div>
              <h2 className="text-2xl font-bold mb-4">
                {t("confirmation_records")}
              </h2>
              {confirmationRecords?.length === 0 ? (
                <NoData message={t("no_confirmation_records_found")} />
              ) : (
                <StaffGrid>
                  {confirmationRecords.map((record) => (
                    <ConfirmationCard
                      key={record.id}
                      name={record.staff.asdc_client_staff_name}
                      profilePicUrl={record.staff.asdc_client_profile_pic_url}
                      createdAt={record.createdAt}
                      status={record.status}
                    />
                  ))}
                </StaffGrid>
              )}
            </div>
          )}
        </>
      )}

      {!isLoading && (
        <div className="mt-6">
          {displayMode === "attendance" && pagination.attendanceCount > 0 && (
            <ReactPaginate
              className="pagination_table"
              containerClassName={"pagination"}
              activeClassName={"active"}
              pageClassName={"page-item"}
              forcePage={pagination.page - 1}
              onPageChange={(event) => {
                setPagination((prev) => ({ ...prev, page: event.selected + 1 }));
              }}
              breakLabel="..."
              pageCount={Math.ceil(pagination.attendanceCount / pagination.pageItemsCount)}
              previousLabel={
                pagination.page === 1 ? null : (
                  <div
                    style={{
                      backgroundColor: "#B8C1CC",
                      width: "36px",
                      height: "36px",
                    }}
                    className="flex items-center justify-center rounded-full text-white"
                  >
                    <GrFormPrevious size={28} />
                  </div>
                )
              }
              nextLabel={
                pagination.page === Math.ceil(pagination.attendanceCount / pagination.pageItemsCount) ? null : (
                  <div
                    style={{
                      backgroundColor: "#B8C1CC",
                      width: "36px",
                      height: "36px",
                    }}
                    className="flex items-center justify-center rounded-full text-white"
                  >
                    <GrFormNext size={28} />
                  </div>
                )
              }
            />
          )}
          
          {displayMode === "confirmation" && pagination.confirmationCount > 0 && (
            <ReactPaginate
              className="pagination_table"
              containerClassName={"pagination"}
              activeClassName={"active"}
              pageClassName={"page-item"}
              forcePage={pagination.page - 1}
              onPageChange={(event) => {
                setPagination((prev) => ({ ...prev, page: event.selected + 1 }));
              }}
              breakLabel="..."
              pageCount={Math.ceil(pagination.confirmationCount / pagination.pageItemsCount)}
              previousLabel={
                pagination.page === 1 ? null : (
                  <div
                    style={{
                      backgroundColor: "#B8C1CC",
                      width: "36px",
                      height: "36px",
                    }}
                    className="flex items-center justify-center rounded-full text-white"
                  >
                    <GrFormPrevious size={28} />
                  </div>
                )
              }
              nextLabel={
                pagination.page === Math.ceil(pagination.confirmationCount / pagination.pageItemsCount) ? null : (
                  <div
                    style={{
                      backgroundColor: "#B8C1CC",
                      width: "36px",
                      height: "36px",
                    }}
                    className="flex items-center justify-center rounded-full text-white"
                  >
                    <GrFormNext size={28} />
                  </div>
                )
              }
            />
          )}
        </div>
      )}
    </div>
  );
}
